import { IconButton, Popover, Typography, useTheme } from '@mui/material'
import {
    casOpenBg,
    casTextColor,
    marketStatusBlue,
    marketStatusGreen,
    marketStatusOpenBg,
    postMarketOpenBg,
    preMarketOpenBg,
    preMarketTextColor
} from '../../assets/colors'
import Morning from '../../assets/icons/Morning'
import MarketOpen from '../../assets/icons/MarketOpen'
import MarketClosed from '../../assets/icons/MarketClosed'
import PostClose from '../../assets/icons/PostClose'
import { TradingSession } from '../../proto/generated/trading-calendar'
import MarketStatusBar from './Slider'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../selectors/home'
import { NonInteractiveTooltip } from '../../components/custom'
import { selectExchangeTradingSessions, selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { darkHoverBtnColor, lightHoverBtnColor } from '../../theme/theme'
import { constructCalendarDataLookup, getTradingSessionsForDate } from '../../util/chart'
import { toDayStart } from '../../util/dateTime'
import {
    anchorCalendarSessionTemplateToDay,
    resolveLiveTradingSessionsForSecurity,
    resolveTradingSessionScope
} from '../../util/tradingSessionSelection'

const DAY_MS = 24 * 60 * 60 * 1000

const MarketStatus = () => {
    const theme = useTheme()
    const closedBg = theme.palette.mode === 'dark' ? darkHoverBtnColor : lightHoverBtnColor
    const { expressionMode, primary, underlying, expressionSecurities } = useSelector(selectPrimarySecurity)
    // Use the chart security itself (exchange/segment/partOfCAS) — not a different underlying.
    const mainSecurity = expressionMode ? expressionSecurities[0] : (primary || underlying)
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)
    const tradingCalendarData = useSelector(selectTradingCalendarDataByExchange(mainSecurity?.exchange))
    const sessionScope = useMemo(
        () => resolveTradingSessionScope(mainSecurity),
        [mainSecurity?.segment, mainSecurity?.partOfCAS]
    )
    const calendarData = useMemo(
        () => constructCalendarDataLookup(tradingCalendarData, mainSecurity),
        [tradingCalendarData, mainSecurity?.exchange, mainSecurity?.segment, mainSecurity?.partOfCAS]
    )
    // Live SuperChart may only include DEFAULT; CAS securities fall back to calendar templates.
    const tradingSessions = useMemo(
        () =>
            resolveLiveTradingSessionsForSecurity(
                exchangeTradingSessions,
                tradingCalendarData,
                mainSecurity
            ),
        [
            exchangeTradingSessions,
            tradingCalendarData,
            mainSecurity?.exchange,
            mainSecurity?.segment,
            mainSecurity?.partOfCAS
        ]
    )
    const [marketOpen, setMarketOpen] = useState<boolean>()
    const [preMarketOpen, setPreMarketOpen] = useState<boolean>()
    const [casOpen, setCasOpen] = useState<boolean>()
    const [postMarketOpen, setPostMarketOpen] = useState<boolean>()
    const [holiday, setHoliday] = useState<boolean>()
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
    const [message, setMessage] = useState<{ lead: string; highlight: string; trail?: string }>()
    const [displaySession, setDisplaySession] = useState<TradingSession>()
    const open = Boolean(anchorEl)
    const [updatedKey, setUpdatedKey] = useState<number>(0)

    const nextTradingDayStartMs = useCallback((nowMs: number) => {
        for (let d = 1; d <= 10; d++) {
            const dayMs = toDayStart(nowMs).getTime() + d * DAY_MS
            const sessions = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)
            if (sessions && sessions.length) return dayMs
        }
        return undefined
    }, [calendarData])

    // Epoch ms of the next pre-open: prefer the live exchange sessions, else derive from the trading calendar.
    const computeNextPreOpenMs = useCallback((nowMs: number, sessions: TradingSession[]): number | undefined => {
        const futurePreOpens = sessions.map((s) => s.preOpenStartTime).filter((t) => t > nowMs)
        if (futurePreOpens.length) return Math.min(...futurePreOpens)
        const dayMs = toDayStart(nowMs).getTime()
        const todayTemplate = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)?.[0]
        if (todayTemplate) {
            const pre = dayMs + ((todayTemplate.preOpenStartTime || todayTemplate.startTime) || 0) * 1000
            if (pre > nowMs) return pre
        }
        const nextDayMs = nextTradingDayStartMs(nowMs)
        if (nextDayMs) {
            const nextTemplate = getTradingSessionsForDate(Math.floor(nextDayMs / 1000), calendarData)?.[0]
            if (nextTemplate) return nextDayMs + ((nextTemplate.preOpenStartTime || nextTemplate.startTime) || 0) * 1000
        }
        return undefined
    }, [calendarData, nextTradingDayStartMs])

    // A session to draw the slider for when the live exchange data has none (e.g. weekends/holidays).
    const buildCalendarSession = useCallback((nowMs: number): TradingSession | undefined => {
        const dayMs = toDayStart(nowMs).getTime()
        const todayTemplate = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)?.[0]
        if (todayTemplate) return anchorCalendarSessionTemplateToDay(dayMs, todayTemplate)
        // Non-trading day: no drawable windows (Slider skips zero start/end).
        return {
            type: '',
            name: '',
            startTime: 0,
            endTime: 0,
            preOpenStartTime: 0,
            preOpenEndTime: 0,
            postCloseStartTime: 0,
            postCloseEndTime: 0,
            segment: mainSecurity?.segment ?? 0,
            scope: sessionScope,
            applicableFrom: '',
            applicableTo: '',
            casStartTime: 0,
            casEndTime: 0
        }
    }, [calendarData, mainSecurity?.segment, sessionScope])

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    useEffect(() => {
        if (!tradingSessions.length || !mainSecurity) return
        const timeoutIds: ReturnType<typeof setTimeout>[] = []
        const nowTime = Date.now()
        for (let i = 0; i < tradingSessions.length; i++) {
            const tradingSession = tradingSessions[i]
            for (const boundary of [
                tradingSession.preOpenStartTime,
                tradingSession.preOpenEndTime,
                tradingSession.startTime,
                tradingSession.endTime,
                tradingSession.casStartTime,
                tradingSession.casEndTime,
                tradingSession.postCloseStartTime,
                tradingSession.postCloseEndTime
            ]) {
                if (boundary && nowTime < boundary) {
                    timeoutIds.push(setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, boundary - nowTime))
                }
            }
        }
        return () => {
            timeoutIds.forEach(id => clearTimeout(id))
        }
    }, [tradingSessions, mainSecurity])

    const formatDuration = (durationMs: number) => {
        const totalSeconds = Math.max(0, Math.floor(durationMs / 1000))
        const days = Math.floor(totalSeconds / 86400)
        const hours = Math.floor((totalSeconds % 86400) / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        const part = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`
        if (totalSeconds >= 86400) return `${part(days, 'day')} and ${part(hours, 'hour')}`
        if (totalSeconds >= 3600) return `${part(hours, 'hour')} and ${part(minutes, 'minute')}`
        if (totalSeconds >= 300) return part(minutes, 'minute')
        if (minutes === 0) return part(seconds, 'second')
        return `${part(minutes, 'minute')} and ${part(seconds, 'second')}`
    }

    useEffect(() => {
        const nowMs = Date.now()
        if (!mainSecurity) return
        const sessions = tradingSessions
        let marketOpen = false
        let preMarketOpen = false
        let casOpen = false
        let postMarketOpen = false
        let holiday = false
        let message: { lead: string; highlight: string; trail?: string } = { lead: '', highlight: '' }
        let activeSession: TradingSession | undefined

        for (const tradingSession of sessions) {
            const hasCas = !!(tradingSession.casStartTime && tradingSession.casEndTime)
            if (nowMs >= tradingSession.preOpenStartTime && nowMs <= tradingSession.preOpenEndTime) {
                preMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Pre-market trading is open.',
                    highlight: `It'll go to regular trading in ${formatDuration(tradingSession.startTime - nowMs)}.`
                }
                break
            }
            if (nowMs >= tradingSession.startTime && nowMs <= tradingSession.endTime) {
                marketOpen = true
                activeSession = tradingSession
                const remainingMs = tradingSession.endTime - nowMs
                const nextPhase = hasCas ? 'closing auction' : 'post-market trading'
                if (remainingMs < 30 * 60 * 1000) {
                    message = {
                        lead: 'Market is open.',
                        highlight: `It'll go to ${nextPhase} in ${formatDuration(remainingMs)}.`
                    }
                } else {
                    message = {
                        lead: 'All\'s well — market is open.',
                        highlight: `It'll go to ${nextPhase} in ${formatDuration(remainingMs)}.`
                    }
                }
                break
            }
            if (hasCas && nowMs >= tradingSession.casStartTime && nowMs <= tradingSession.casEndTime) {
                casOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Closing auction is open.',
                    highlight: `It'll go to post-market trading in ${formatDuration(tradingSession.casEndTime - nowMs)}.`
                }
                break
            }
            if (hasCas && nowMs > tradingSession.endTime && nowMs < tradingSession.casStartTime) {
                casOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Regular trading has ended.',
                    highlight: `It'll go to closing auction in ${formatDuration(tradingSession.casStartTime - nowMs)}.`
                }
                break
            }
            if (nowMs > tradingSession.endTime && nowMs < tradingSession.postCloseStartTime
                && !(hasCas && nowMs <= tradingSession.casEndTime)) {
                postMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Regular trading has ended.',
                    highlight: `It'll go to post-market trading in ${formatDuration(tradingSession.postCloseStartTime - nowMs)}.`
                }
                break
            }
            if (nowMs >= tradingSession.postCloseStartTime && nowMs <= tradingSession.postCloseEndTime) {
                postMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Post-market trading is open.',
                    highlight: `It'll close in ${formatDuration(tradingSession.postCloseEndTime - nowMs)}.`
                }
                break
            }
        }

        if (!activeSession) {
            const nextPreOpenMs = computeNextPreOpenMs(nowMs, sessions)
            const openDuration = nextPreOpenMs ? formatDuration(nextPreOpenMs - nowMs) : ''
            // Trading holiday: a weekday with no scheduled sessions in the calendar (weekends are not "holidays").
            const dayMs = toDayStart(nowMs).getTime()
            const weekday = new Date(nowMs).getDay()
            const isWeekend = weekday === 0 || weekday === 6
            const todaySessions = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)
            holiday = !isWeekend && (!todaySessions || todaySessions.length === 0)
            message = holiday
                ? { lead: 'No trading sessions today.', highlight: nextPreOpenMs ? `It'll open for pre-market trading in ${openDuration}.` : '' }
                : { lead: 'Market is closed.', highlight: nextPreOpenMs ? `It'll open for pre-market trading in ${openDuration}.` : '' }
        }

        const todayStr = new Date(nowMs).toDateString()
        const sameDaySession = sessions.find((s) => new Date(s.startTime).toDateString() === todayStr)
        const nextFutureSession = sessions
            .filter((s) => s.preOpenStartTime > nowMs)
            .sort((a, b) => a.preOpenStartTime - b.preOpenStartTime)[0]

        setMarketOpen(marketOpen)
        setPreMarketOpen(preMarketOpen)
        setCasOpen(casOpen)
        setPostMarketOpen(postMarketOpen)
        setHoliday(holiday)
        setMessage(message)
        setDisplaySession(activeSession || sameDaySession || nextFutureSession || sessions[0] || buildCalendarSession(nowMs))
    }, [tradingSessions, mainSecurity, updatedKey, computeNextPreOpenMs, buildCalendarSession, calendarData])

    useEffect(() => {
        if (!open) return
        setUpdatedKey((prev) => prev + 1)
        const id = setInterval(() => setUpdatedKey((prev) => prev + 1), 1000)
        return () => clearInterval(id)
    }, [open])

    const statusLabel = preMarketOpen
        ? 'Pre-market is open'
        : marketOpen
            ? 'Market Open'
            : casOpen
                ? 'Closing auction is open'
                : postMarketOpen
                    ? 'Post-close is open'
                    : holiday
                        ? 'Market Holiday'
                        : 'Market Closed'
    const statusTitle = preMarketOpen
        ? 'Pre-market'
        : marketOpen
            ? 'Market open'
            : casOpen
                ? 'Closing auction'
                : postMarketOpen
                    ? 'Post-close'
                    : holiday
                        ? 'Market holiday'
                        : 'Market closed'
    const statusBg = preMarketOpen
        ? preMarketOpenBg
        : marketOpen
            ? marketStatusOpenBg
            : casOpen
                ? casOpenBg
                : postMarketOpen
                    ? postMarketOpenBg
                    : closedBg
    const statusColor = preMarketOpen
        ? preMarketTextColor
        : marketOpen
            ? marketStatusGreen
            : casOpen
                ? casTextColor
                : postMarketOpen
                    ? marketStatusBlue
                    : 'text.secondary'
    const statusIcon = preMarketOpen
        ? <Morning />
        : marketOpen || casOpen
            ? <MarketOpen />
            : postMarketOpen
                ? <PostClose />
                : <MarketClosed />

    const tooltipContent = useMemo(() => (
        <Typography variant="caption">{statusLabel}</Typography>
    ), [statusLabel])

    const tooltipChildren = useMemo(() => {
        return <IconButton
            style={{ padding: '0px' }}
            disableRipple={true}
            data-event={'Market Status Icon Clicked'}
            data-event-source={'stockbar'}
            onClick={handleClick}>
            <div
                style={{
                    display: 'flex',
                    borderRadius: '50%',
                    color: theme.palette.text.secondary,
                    backgroundColor: marketOpen ? 'transparent' : statusBg
                }}>
                {statusIcon}
            </div>
        </IconButton>
    }, [handleClick, marketOpen, statusBg, statusIcon, theme.palette.text.secondary])

    const popover = useMemo(() => (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            style={{ marginLeft: '-16px', marginTop: '8px' }}
            slotProps={{
                paper: {
                    style: { borderRadius: '6px' }
                }
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
        >
            <div style={{ width: '340px', maxWidth: '340px' }}>
                <div style={{
                    display: 'flex',
                    paddingTop: '14px',
                    paddingBottom: '14px',
                    paddingLeft: '16px',
                    paddingRight: '20px'
                }}>
                    <div
                        style={{
                            display: 'flex',
                            alignSelf: 'flex-start',
                            padding: 0,
                            borderRadius: '50%',
                            color: theme.palette.text.secondary,
                            // Market-open icon already includes its own halo; skip the tinted chip.
                            backgroundColor: marketOpen ? 'transparent' : statusBg,
                            marginTop: '2px'
                        }}>
                        {statusIcon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px', minWidth: 0, flex: 1 }}>
                        <Typography variant="body1"
                                    style={{ fontWeight: 600, lineHeight: 1.25 }}
                                    sx={{ color: statusColor }}>{statusTitle}</Typography>
                        <Typography variant="body2" sx={{ marginTop: '4px', color: 'text.secondary', lineHeight: 1.45 }}>
                            {message?.lead}{message?.lead ? ' ' : ''}
                            <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{message?.highlight}</Typography>
                            {message?.trail}
                        </Typography>
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: '18px'
                        }}>
                            {displaySession && <MarketStatusBar tradingSession={displaySession} />}
                        </div>
                    </div>
                </div>
            </div>
        </Popover>
    ), [open, anchorEl, handleClose, marketOpen, statusBg, statusIcon, statusColor, statusTitle, message, displaySession, theme.palette.text.secondary])

    return (<>
        <NonInteractiveTooltip title={tooltipContent} arrow={true} placement="top">
            {tooltipChildren}
        </NonInteractiveTooltip>
        {popover}
    </>)
}
export default memo(MarketStatus)